import { SvgIcon, SvgIconProps } from "@mui/material";

/**
 * Line icons traced from the `STEP 2 : APPROVERS AND SIGNERS` block of
 * `Create Sign Request - Me & Others.dc.html`. The design draws each one as an
 * inline stroked SVG rather than a Material glyph, so the shapes live here
 * instead of pulling near-matches from `@mui/icons-material`.
 *
 * All of them inherit `currentColor`; size comes from the caller's `sx`.
 */

const strokeProps = {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round" as const,
};

const joinedStrokeProps = {
  ...strokeProps,
  strokeLinejoin: "round" as const,
};

/** Checked state of the "Set Signer's order" box. */
export const TickGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...joinedStrokeProps} strokeWidth={3.2} d="M5 13l4 4L19 7" />
  </SvgIcon>
);

/** Circle-i beside the order checkbox and above the approvers list. */
export const InfoCircleGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <circle {...strokeProps} strokeWidth={2} cx="12" cy="12" r="9" />
    <path {...strokeProps} strokeWidth={2} d="M12 8h.01" />
    <path {...strokeProps} strokeWidth={2} d="M12 11v5" />
  </SvgIcon>
);

/** Signers tab / single-approver bust. */
export const PersonGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <circle {...strokeProps} strokeWidth={2} cx="12" cy="8" r="4" />
    <path {...strokeProps} strokeWidth={2} d="M4 21c0-4 3.6-6 8-6s8 2 8 6" />
  </SvgIcon>
);

/** Approvers tab / approver-group mark. */
export const PeopleGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <circle {...strokeProps} strokeWidth={2} cx="9" cy="8" r="3.5" />
    <path {...strokeProps} strokeWidth={2} d="M2.5 20c0-3.3 2.9-5 6.5-5s6.5 1.7 6.5 5" />
    <path {...strokeProps} strokeWidth={2} d="M16 4.6a3.5 3.5 0 0 1 0 6.8" />
    <path {...strokeProps} strokeWidth={2} d="M17.5 15.4c2.4.6 4 2 4 4.6" />
  </SvgIcon>
);

/** "Add signer" / "Add member" plus. */
export const PlusGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...strokeProps} strokeWidth={2.5} d="M12 5v14" />
    <path {...strokeProps} strokeWidth={2.5} d="M5 12h14" />
  </SvgIcon>
);

/** Split-button and dropdown caret. */
export const CaretDownGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...strokeProps} strokeWidth={2.5} d="M6 9l6 6 6-6" />
  </SvgIcon>
);

/** "Move to earlier level" chevron. */
export const CaretUpGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...strokeProps} strokeWidth={2.5} d="M6 15l6-6 6 6" />
  </SvgIcon>
);

/** Six-dot drag affordance on the left of a signer row. */
export const DragDotsGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <circle cx="9" cy="5" r="1.7" fill="currentColor" />
    <circle cx="15" cy="5" r="1.7" fill="currentColor" />
    <circle cx="9" cy="12" r="1.7" fill="currentColor" />
    <circle cx="15" cy="12" r="1.7" fill="currentColor" />
    <circle cx="9" cy="19" r="1.7" fill="currentColor" />
    <circle cx="15" cy="19" r="1.7" fill="currentColor" />
  </SvgIcon>
);

/** Security shield on the security trigger and the OTP note. */
export const ShieldGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path
      {...joinedStrokeProps}
      strokeWidth={2}
      d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z"
    />
  </SvgIcon>
);

/** Padlock on the access-code editor. */
export const LockGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <rect
      {...joinedStrokeProps}
      strokeWidth={2}
      x="4"
      y="10"
      width="16"
      height="10"
      rx="2"
    />
    <path {...joinedStrokeProps} strokeWidth={2} d="M8 10V7a4 4 0 0 1 8 0v3" />
  </SvgIcon>
);

/** Remove a recipient. */
export const TrashGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...joinedStrokeProps} strokeWidth={2} d="M3 6h18" />
    <path
      {...joinedStrokeProps}
      strokeWidth={2}
      d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2"
    />
    <path
      {...joinedStrokeProps}
      strokeWidth={2}
      d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"
    />
    <path {...joinedStrokeProps} strokeWidth={2} d="M10 11v6" />
    <path {...joinedStrokeProps} strokeWidth={2} d="M14 11v6" />
  </SvgIcon>
);

/** Delay clock. */
export const ClockGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <circle {...strokeProps} strokeWidth={2} cx="12" cy="12" r="9" />
    <path {...strokeProps} strokeWidth={2} d="M12 7v5l3 3" />
  </SvgIcon>
);

/** Close / remove-member cross. */
export const CloseGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...strokeProps} strokeWidth={2.5} d="M18 6L6 18" />
    <path {...strokeProps} strokeWidth={2.5} d="M6 6l12 12" />
  </SvgIcon>
);

/** Handset on the SMS / WhatsApp delivery phone box. */
export const PhoneGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path
      {...joinedStrokeProps}
      strokeWidth={2}
      d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 2 .7 2.9a2 2 0 0 1-.4 2.1L8.1 10a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.9.6 2.9.7a2 2 0 0 1 1.6 2z"
    />
  </SvgIcon>
);

/** Pen — the role trigger when the signer is set to "Need to Sign". */
export const PenGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...strokeProps} strokeWidth={2} d="M12 20h9" />
    <path
      {...joinedStrokeProps}
      strokeWidth={2}
      d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"
    />
  </SvgIcon>
);

/** Eye — the role trigger when the recipient "Receives a copy" (CC). */
export const EyeGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path
      {...joinedStrokeProps}
      strokeWidth={2}
      d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"
    />
    <circle {...strokeProps} strokeWidth={2} cx="12" cy="12" r="2.5" />
  </SvgIcon>
);

/** Chain link — the delivery-mode trigger for "Send a signing link". */
export const LinkGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path
      {...joinedStrokeProps}
      strokeWidth={2}
      d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
    />
    <path
      {...joinedStrokeProps}
      strokeWidth={2}
      d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
    />
  </SvgIcon>
);

/** Device screen — the delivery-mode trigger for "Sign in person". */
export const MonitorGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <rect
      {...joinedStrokeProps}
      strokeWidth={2}
      x="2"
      y="3"
      width="20"
      height="14"
      rx="2"
    />
    <path {...strokeProps} strokeWidth={2} d="M8 21h8" />
    <path {...strokeProps} strokeWidth={2} d="M12 17v4" />
  </SvgIcon>
);

/** "Same as delivery number" copy affordance. */
export const CopyGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <rect
      {...joinedStrokeProps}
      strokeWidth={2}
      x="9"
      y="9"
      width="13"
      height="13"
      rx="2"
    />
    <path
      {...joinedStrokeProps}
      strokeWidth={2}
      d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
    />
  </SvgIcon>
);
